import React, { useEffect, useState } from 'react';
import api from '../../utils/api';

const STATUS_COLORS = { active: 'active', suspended: 'inactive', blocked: 'cancelled' };

const toSlug = str => str.toLowerCase()
  .normalize('NFD').replace(/[̀-ͯ]/g, '')
  .replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

export default function BusinessesPage() {
  const [businesses, setBusinesses] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(null);
  const [waState, setWaState] = useState(null);   // estado de conexión Evolution
  const [waQr, setWaQr] = useState(null);         // imagen QR base64
  const [waBusy, setWaBusy] = useState(false);
  const [waMsg, setWaMsg] = useState('');
  const [waInfo, setWaInfo] = useState(null);     // { provider, instance, phone }

  const load = () => {
    setLoading(true);
    Promise.all([api.get('/businesses'), api.get('/plans')])
      .then(([b, p]) => { setBusinesses(b.data); setPlans(p.data); })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const EMPTY = { name: '', slug: '', legal_name: '', nit: '', address: '', city: '', department: '', phone: '', email: '', plan_id: '', negotiation_type: 'plaza', negotiation_value: '' };

  const openCreate = () => { setForm(EMPTY); setModal('create'); setError(''); };
  const openEdit = b => {
    // El API devuelve las columnas en español (nombre, direccion, ciudad...).
    // Aquí se traducen a las claves que usan el formulario y el PUT; sin esto
    // los campos salían vacíos al editar y al guardar se borraban en la BD.
    setForm({
      ...b,
      name:              b.nombre || b.name || '',
      slug:              b.slug || '',
      legal_name:        b.razon_social || b.legal_name || '',
      nit:               b.nit || '',
      address:           b.direccion || b.address || '',
      city:              b.ciudad || b.city || '',
      department:        b.departamento || b.department || '',
      phone:             b.telefono || b.phone || '',
      email:             b.email || '',
      website:           b.sitio_web || b.website || '',
      negotiation_type:  b.tipo_negociacion || b.negotiation_type || 'plaza',
      negotiation_value: b.valor_negociacion ?? b.negotiation_value ?? '',
      logo_url:          b.url_logo || b.logo_url || '',
      plan_id:           b.plan_id || '',
      whatsapp_provider: b.whatsapp_provider || 'evolution',
    });
    setModal(b);
    setError('');
    setWaQr(null); setWaMsg(''); setWaInfo(null);
    setWaState(null);
    api.get(`/whatsapp/${b.id}/status`)
      .then(r => { setWaState(r.data.state); setWaInfo(r.data); })
      .catch(() => setWaState('no_disponible'));
  };

  const waVincular = async (businessId) => {
    setWaBusy(true); setWaMsg(''); setWaQr(null);
    try {
      await api.post(`/whatsapp/${businessId}/instance`);
      const qrRes = await api.get(`/whatsapp/${businessId}/qr`);
      if (qrRes.data.qr) {
        setWaQr(qrRes.data.qr);
        setWaMsg('Escanea el QR con el WhatsApp del negocio (Dispositivos vinculados). El QR caduca en ~40 segundos.');
      } else {
        setWaMsg('No hay QR pendiente — la instancia parece estar ya conectada. Usa "Verificar conexión".');
      }
    } catch (err) {
      setWaMsg(err.response?.data?.error || 'Error comunicándose con Evolution API');
    } finally { setWaBusy(false); }
  };

  const waVerificar = async (businessId) => {
    setWaBusy(true);
    try {
      const r = await api.get(`/whatsapp/${businessId}/status`);
      setWaState(r.data.state); setWaInfo(r.data);
      if (r.data.state === 'open') { setWaQr(null); setWaMsg('✅ WhatsApp conectado.'); }
      else setWaMsg(`Estado actual: ${r.data.state}`);
    } catch { setWaMsg('No se pudo consultar el estado'); }
    finally { setWaBusy(false); }
  };

  const waDesvincular = async (businessId) => {
    if (!window.confirm('¿Cerrar la sesión de WhatsApp de este negocio? Dejará de enviar mensajes hasta vincular de nuevo.')) return;
    setWaBusy(true);
    try {
      await api.delete(`/whatsapp/${businessId}/session`);
      setWaState('close'); setWaQr(null); setWaMsg('Sesión cerrada.');
    } catch (err) { setWaMsg(err.response?.data?.error || 'No se pudo cerrar la sesión'); }
    finally { setWaBusy(false); }
  };

  const handleNameChange = val => {
    setForm(f => ({ ...f, name: val, slug: f.slug ? f.slug : toSlug(val) }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (modal === 'create') {
        await api.post('/businesses', form);
      } else {
        await api.put(`/businesses/${modal.id}`, form);
        await api.patch(`/businesses/${modal.id}/whatsapp`, {
          whatsapp_provider: form.whatsapp_provider || 'evolution',
          ultramsg_instance_id: form.ultramsg_instance_id || '',
          ultramsg_token: form.ultramsg_token || '',
        });
        if (form.plan_id) {
          const planRes = await api.patch(`/businesses/${modal.id}/plan`, { plan_id: form.plan_id });
          const msg = planRes.data?.message || '';
          if (msg.toLowerCase().includes('sucursal')) {
            // El backend informó que se inactivaron sucursales
            setTimeout(() => alert(msg), 100);
          }
        }
      }
      setModal(null);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const changeStatus = async (id, status) => {
    await api.patch(`/businesses/${id}/status`, { status });
    load();
  };

  const copyLink = (slug, id) => {
    navigator.clipboard.writeText(`${window.location.origin}/book/${slug}`);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <>
      <div className="page-header">
        <div className="page-header-text">
          <h2>Negocios</h2>
          <p>Administra todos los negocios registrados en la plataforma</p>
        </div>
        <button className="btn btn-primary" onClick={openCreate}>+ Nuevo Negocio</button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 48 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
      ) : (
        <div className="card">
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Negocio</th>
                  <th>Link de Agendamiento</th>
                  <th>Ciudad</th>
                  <th>Plan</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {businesses.map(b => (
                  <tr key={b.id}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{b.nombre}</div>
                      {b.email && <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>{b.email}</div>}
                    </td>
                    <td>
                      {b.slug ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <code style={{ fontSize: '0.75rem', color: 'var(--color-gold)', background: 'var(--color-surface-raised)', padding: '2px 6px', borderRadius: 4 }}>
                            /book/{b.slug}
                          </code>
                          <button
                            className="btn btn-sm btn-ghost"
                            style={{ padding: '2px 8px', fontSize: '0.72rem' }}
                            onClick={() => copyLink(b.slug, b.id)}
                          >
                            {copied === b.id ? '✓ Copiado' : 'Copiar'}
                          </button>
                        </div>
                      ) : <span style={{ color: 'var(--color-text-faint)' }}>—</span>}
                    </td>
                    <td>{b.ciudad || '—'}</td>
                    <td><span className="badge badge-gold">{b.plan_name || '—'}</span></td>
                    <td><span className={`badge badge-${STATUS_COLORS[b.estado] || 'inactive'}`}>{b.estado}</span></td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button className="btn btn-sm btn-secondary" onClick={() => openEdit(b)}>✏️</button>
                        {b.estado === 'active'
                          ? <button className="btn btn-sm btn-danger" onClick={() => changeStatus(b.id, 'suspended')}>Suspender</button>
                          : <button className="btn btn-sm btn-secondary" onClick={() => changeStatus(b.id, 'active')}>Activar</button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {modal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 660 }}>
            <div className="modal-header">
              <span className="modal-title">{modal === 'create' ? 'Nuevo Negocio' : 'Editar Negocio'}</span>
              <button className="btn btn-ghost btn-icon" onClick={() => setModal(null)}>✕</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {error && <div className="alert alert-error" style={{ marginBottom: 12 }}>{error}</div>}
                <div className="form-grid">
                  <div className="form-group" style={{ gridColumn: '1/-1' }}>
                    <label className="form-label">Nombre comercial *</label>
                    <input
                      className="form-input"
                      required
                      value={form.name || ''}
                      onChange={e => handleNameChange(e.target.value)}
                    />
                  </div>

                  {/* Slug / Link único */}
                  <div className="form-group" style={{ gridColumn: '1/-1' }}>
                    <label className="form-label">
                      Link único de agendamiento *
                      <span style={{ fontWeight: 400, color: 'var(--color-text-muted)', fontSize: '0.78rem', marginLeft: 8 }}>
                        (solo letras, números y guiones)
                      </span>
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ color: 'var(--color-text-muted)', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                        /book/
                      </span>
                      <input
                        className="form-input"
                        required
                        placeholder="mi-barberia"
                        value={form.slug || ''}
                        onChange={e => setForm(f => ({ ...f, slug: toSlug(e.target.value) }))}
                        style={{ flex: 1 }}
                      />
                    </div>
                    {form.slug && (
                      <div style={{ marginTop: 6, fontSize: '0.8rem', color: 'var(--color-gold)' }}>
                        Link: <strong>{window.location.origin}/book/{form.slug}</strong>
                      </div>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="form-label">Razón social</label>
                    <input className="form-input" value={form.legal_name ?? ''} onChange={e => setForm(f => ({ ...f, legal_name: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">NIT</label>
                    <input className="form-input" value={form.nit || ''} onChange={e => setForm(f => ({ ...f, nit: e.target.value }))} />
                  </div>
                  <div className="form-group" style={{ gridColumn: '1/-1' }}>
                    <label className="form-label">Dirección</label>
                    <input className="form-input" value={form.address ?? ''} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Ciudad</label>
                    <input className="form-input" value={form.city ?? ''} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Teléfono</label>
                    <input className="form-input" value={form.phone ?? ''} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email *</label>
                    <input type="email" className="form-input" required value={form.email || ''} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Plan {modal === 'create' ? '*' : ''}</label>
                    <select className="form-select" value={form.plan_id || ''} onChange={e => setForm(f => ({ ...f, plan_id: e.target.value }))}>
                      <option value="">Sin plan</option>
                      {plans.map(p => <option key={p.id} value={p.id}>{p.nombre_visible}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Modelo de negociación</label>
                    <select className="form-select" value={form.negotiation_type || 'plaza'} onChange={e => setForm(f => ({ ...f, negotiation_type: e.target.value }))}>
                      <option value="plaza">Por plaza</option>
                      <option value="percentage">Por porcentaje</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Valor / %</label>
                    <input type="number" className="form-input" value={form.negotiation_value ?? ''} onChange={e => setForm(f => ({ ...f, negotiation_value: e.target.value }))} />
                  </div>

                  {modal !== 'create' && (
                    <>
                      <div style={{ gridColumn: '1/-1', marginTop: '0.5rem', paddingTop: '0.75rem', borderTop: '1px solid var(--color-border)', fontWeight: 600, fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>
                        📱 WhatsApp del negocio — los mensajes de confirmación salen del número vinculado aquí
                      </div>

                      <div className="form-group">
                        <label className="form-label">Proveedor</label>
                        <select
                          className="form-select"
                          value={form.whatsapp_provider || 'evolution'}
                          onChange={e => { setForm(f => ({ ...f, whatsapp_provider: e.target.value })); setWaQr(null); setWaMsg('Guarda los cambios para aplicar el proveedor y luego vincula.'); }}
                        >
                          <option value="evolution">Evolution API (servidor propio)</option>
                          <option value="ultramsg">UltraMsg (servicio pago por número)</option>
                        </select>
                      </div>
                      <div className="form-group" />

                      {form.whatsapp_provider === 'ultramsg' && (
                        <>
                          <div className="form-group">
                            <label className="form-label">UltraMsg Instance ID</label>
                            <input className="form-input" placeholder="instance123456" value={form.ultramsg_instance_id || ''} onChange={e => setForm(f => ({ ...f, ultramsg_instance_id: e.target.value }))} />
                          </div>
                          <div className="form-group">
                            <label className="form-label">UltraMsg Token</label>
                            <input className="form-input" placeholder="token de la instancia" value={form.ultramsg_token || ''} onChange={e => setForm(f => ({ ...f, ultramsg_token: e.target.value }))} />
                          </div>
                          <div style={{ gridColumn: '1/-1', fontSize: '0.78rem', color: 'var(--color-text-muted)', marginTop: -4, marginBottom: 4 }}>
                            Crea la instancia en ultramsg.com, copia aquí el Instance ID y el Token, pulsa <strong>Guardar</strong> y luego vincula el número con el QR.
                          </div>
                        </>
                      )}

                      <div style={{ gridColumn: '1/-1' }}>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
                          <span className={`badge badge-${waState === 'open' ? 'success' : waState === 'connecting' ? 'warning' : 'secondary'}`}>
                            {waState === 'open' ? '✅ Conectado'
                              : waState === 'connecting' ? '🔄 Conectando'
                              : waState === 'close' ? '🔌 Desconectado'
                              : waState === 'sin_instancia' ? 'Sin vincular'
                              : waState === 'no_disponible' ? 'Servidor Evolution no disponible'
                              : waState || 'Consultando...'}
                          </span>
                          {waInfo?.instance && (
                            <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                              instancia: {waInfo.instance}{waInfo.phone ? ` · número: ${waInfo.phone}` : ''}
                            </span>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          <button type="button" className="btn btn-primary" style={{ fontSize: '0.8rem' }} disabled={waBusy} onClick={() => waVincular(modal.id)}>
                            {waBusy ? '...' : '📷 Vincular WhatsApp (QR)'}
                          </button>
                          <button type="button" className="btn btn-secondary" style={{ fontSize: '0.8rem' }} disabled={waBusy} onClick={() => waVerificar(modal.id)}>
                            Verificar conexión
                          </button>
                          {waState === 'open' && (
                            <button type="button" className="btn btn-ghost" style={{ fontSize: '0.8rem' }} disabled={waBusy} onClick={() => waDesvincular(modal.id)}>
                              Desvincular
                            </button>
                          )}
                        </div>
                        {waMsg && <div style={{ fontSize: '0.8rem', marginTop: 8, color: 'var(--color-text-muted)' }}>{waMsg}</div>}
                        {waQr && (
                          <div style={{ marginTop: 10, textAlign: 'center' }}>
                            <img src={waQr} alt="QR para vincular WhatsApp" style={{ width: 220, height: 220, borderRadius: 8, border: '1px solid var(--color-border)', background: '#fff', padding: 6 }} />
                          </div>
                        )}
                      </div>

                    </>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setModal(null)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? <><div className="spinner" />Guardando...</> : '💾 Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
